import React, { useContext, useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { getRecentlyViewed } from "../../util/recentViewedService";
import { getProductImageUrl, handleProductImageError, applyStoreTitle } from "../../util/storeLogoHelper";
import { loadStoreSettings } from "../../util/storeSettings";
import { loadCategories } from "../../util/storeCatalog";
import { FaSlidersH } from "react-icons/fa";
import SEOHead from "../common/SEOHead";
import SectionRenderer from "../../themes/SectionRenderer";
import { themeButtonCssVars } from "../../themes/themeRegistry";
import { ActiveThemeContext, readActiveThemeCache, readThemeRecord, themeHasSections } from "../../themes/themeCache";

function homeProductLimit(theme) {
  let limit = 8;
  const sections = theme && Array.isArray(theme.sections) ? theme.sections : [];
  sections.forEach((section) => {
    if (!section || section.enabled === false || section.type !== "featured_products") return;
    const count = Number(section.settings && section.settings.count);
    if (count > limit) limit = count;
  });
  if (limit > 24) return 24;
  return limit;
}

export default function Home() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const previewThemeKey = searchParams.get("preview_theme");

  const themeState = useContext(ActiveThemeContext);
  const cachedTheme = previewThemeKey ? readThemeRecord(previewThemeKey) : readActiveThemeCache();
  const contextTheme = themeHasSections(themeState?.theme) ? themeState.theme : null;
  const activeThemeData = contextTheme || (themeHasSections(cachedTheme) ? cachedTheme : null);
  const themeStatus = activeThemeData ? "ready" : themeState?.status || "loading";
  const [categoryList, setCategoryList] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsReady, setProductsReady] = useState(false);
  const [banners, setBanners] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [storeSettings, setStoreSettings] = useState(() => {
    try {
      const raw = localStorage.getItem("store_settings");
      const saved = raw ? JSON.parse(raw) : null;
      return saved && (saved.storeName || saved.logoUrl) ? saved : null;
    } catch {
      return null;
    }
  });

  const productLimit = homeProductLimit(activeThemeData);

  useEffect(() => {
    window.scrollTo(0, 0);
    let ismounted = true;

    loadCategories().then((categories) => {
      if (ismounted) setCategoryList(categories || []);
    });

    loadStoreSettings()
      .then((settings) => {
        if (ismounted && settings && (settings.storeName || settings.logoUrl)) {
          setStoreSettings(settings);
          applyStoreTitle(settings, "");
        }
      })
      .catch(() => {});

    // 5. Fetch Hero Banners
    axios
      .get(`/api/banners`)
      .then((res) => {
        if (ismounted && res.data.status === 200 && res.data.banners) {
          setBanners(res.data.banners);
        }
      })
      .catch(() => {});

    setRecentlyViewed(getRecentlyViewed());
    const refreshRecent = () => {
      if (ismounted) setRecentlyViewed(getRecentlyViewed());
    };
    window.addEventListener("recentlyViewedUpdated", refreshRecent);

    return () => {
      ismounted = false;
      window.removeEventListener("recentlyViewedUpdated", refreshRecent);
    };
  }, [previewThemeKey]);

  useEffect(() => {
    let ismounted = true;
    axios
      .get(`/api/home-products?limit=${productLimit}`)
      .then((res) => {
        if (ismounted && res.data.status === 200) {
          const prods = res.data.products || res.data.data || [];
          setAllProducts(prods);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (ismounted) setProductsReady(true);
      });
    return () => {
      ismounted = false;
    };
  }, [productLimit]);

  // CSS variables computed from the active theme settings
  const themeCssVariables = useMemo(() => {
    if (!activeThemeData?.settings) return {};
    const s = activeThemeData.settings;
    return {
      "--theme-secondary": s.secondaryColor || "#0f172a",
      "--theme-font": s.fontFamily || "'Playfair Display', Georgia, serif",
      "--theme-body-font": s.bodyFontFamily || "'Inter', sans-serif",
      ...themeButtonCssVars(s),
    };
  }, [activeThemeData]);

  const isPreviewMode = Boolean(previewThemeKey || activeThemeData?.is_preview);

  return (
    <div className="storefront-page-root position-relative" style={themeCssVariables}>
      {/* Dynamic SEO Meta */}
      <SEOHead description={storeSettings?.metaDescription} />

      {/* Floating Shopify-Style Preview Mode Bar (Only visible during preview) */}
      {isPreviewMode && (
        <div
          className="position-fixed bottom-0 start-50 translate-middle-x mb-3 px-4 py-2.5 rounded-pill shadow-lg text-white d-flex align-items-center gap-3"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.94)",
            border: "1px solid rgba(56, 189, 248, 0.4)",
            zIndex: 9999,
            backdropFilter: "blur(12px)",
            fontSize: "13px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.5)",
          }}
        >
          <span
            className="badge rounded-pill px-2.5 py-1 text-white"
            style={{ backgroundColor: "#0284c7", fontSize: "11px", fontWeight: "700" }}
          >
            THEME PREVIEW
          </span>
          <span>
            Previewing: <strong>{activeThemeData?.name || previewThemeKey}</strong> (Draft changes are private)
          </span>
          <a
            href="/"
            className="btn btn-sm btn-light rounded-pill px-3 py-1 fw-bold text-dark text-decoration-none shadow-sm"
          >
            Exit Preview
          </a>
          <Link
            to="/admin/theme-editor"
            className="btn btn-sm btn-outline-info rounded-pill px-3 py-1 fw-semibold text-decoration-none"
          >
            <FaSlidersH size={11} className="me-1" /> Customize
          </Link>
        </div>
      )}

      {/* Theme Dynamic Section Renderer */}
      {activeThemeData ? (
        <SectionRenderer
          sections={activeThemeData.sections}
          themeKey={activeThemeData.theme_key || "prestige"}
          globalSettings={activeThemeData.settings || {}}
          categories={categoryList}
          products={allProducts}
          banners={banners}
          storeSettings={storeSettings}
          productsReady={productsReady}
          isEditor={false}
        />
      ) : themeStatus === "error" ? (
        <SectionRenderer
          sections={[
            { id: "sec_hero", type: "hero_banner", enabled: true, settings: {} },
            { id: "sec_collections", type: "collection_list", enabled: true, settings: {} },
            { id: "sec_featured_products", type: "featured_products", enabled: true, settings: {} },
          ]}
          themeKey="prestige"
          globalSettings={{}}
          categories={categoryList}
          products={allProducts}
          banners={banners}
          storeSettings={storeSettings}
          productsReady={productsReady}
          isEditor={false}
        />
      ) : (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-2">Loading storefront...</p>
        </div>
      )}

      {recentlyViewed.length > 0 && (
        <section className="recently-viewed">
          <div className="container">
            <h3 className="recently-viewed-title">Recently Viewed</h3>
            <div className="row g-3">
              {recentlyViewed.slice(0, 4).map((rItem) => {
                const href =
                  rItem.categorySlug && rItem.categorySlug !== "undefined"
                    ? `/collection/${rItem.categorySlug}/${rItem.slug}`
                    : "/collection";
                return (
                  <div key={rItem.id} className="col-6 col-md-3">
                    <div className="product-card-modern">
                      <Link to={href} className="product-card-img-wrap">
                        <img
                          src={getProductImageUrl(rItem)}
                          alt={rItem.name}
                          onError={handleProductImageError}
                        />
                      </Link>
                      <div className="product-card-body">
                        {rItem.categoryName ? (
                          <span className="product-card-category">{rItem.categoryName}</span>
                        ) : null}
                        <Link to={href}>
                          <h4 className="product-card-title">{rItem.name}</h4>
                        </Link>
                        <div className="product-card-price-row">
                          <span className="product-current-price">
                            Rs. {Number(rItem.price || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
